<div class="flex flex-col h-full w-full bg-body-background relative @container">
  <div class="relative flex flex-col gap-6 p-4 @md:p-6 @xl:p-8 w-full max-w-[1500px] mx-auto min-h-full pb-32">
    <!-- HERO SECTION -->
    <header class="flex flex-col @2xl:flex-row @2xl:items-start justify-between gap-6 pt-2 pb-1">
      <div class="flex flex-col gap-2 max-w-2xl">
        <h1 class="text-3xl @md:text-4xl font-extrabold text-heading tracking-tight">
          {{ config.texts.heroTitle || 'Databases' }} &amp; Tables
        </h1>
        <p class="text-secondary-text text-sm @md:text-base leading-relaxed m-0">
          {{ config.texts.heroDescription || 'Manage your databases and data tables' }}
        </p>
      </div>

      <!-- Quick Metrics & Actions -->
      <div class="flex flex-col items-end gap-3 shrink-0">
        <!-- Header Actions Menu (one level up, right-aligned with count table) -->
        <button
          nz-dropdown
          [nzDropdownMenu]="databaseHeaderMenu"
          [nzPlacement]="'bottomRight'"
          nz-tooltip
          [nzTooltipTitle]="config.texts.aiContextBtnTooltip"
          class="w-[38px] h-[38px] rounded-md flex items-center justify-center text-secondary-text bg-component-inner border border-secondary-border hover:bg-hover-default hover:text-primary transition-colors focus:outline-none cursor-pointer shadow-xs"
        >
          <i class="fas fa-ellipsis-v"></i>
        </button>
        <nz-dropdown-menu #databaseHeaderMenu="nzDropdownMenu">
          <ul
            nz-menu
            class="bg-component-inner border border-primary-border shadow-thick rounded-lg min-w-[200px] overflow-hidden p-1"
          >
            <li
              nz-menu-item
              class="cursor-pointer text-sm font-medium transition-colors hover:bg-hover-default text-primary-text rounded-md"
              (click)="openAiContextModal()"
            >
              <div class="flex items-center gap-2 px-1 py-1">
                <i class="fa-light fa-sparkles text-primary"></i>
                <span>{{ config.texts.aiContextBtn || 'AI Context' }}</span>
              </div>
            </li>
          </ul>
        </nz-dropdown-menu>

        <!-- Count Table (Databases & Tables metrics pill) -->
        <div class="flex items-center gap-6 px-6 py-2.5 rounded-2xl bg-component-inner border border-primary-border shadow-sm min-w-[240px] justify-between">
          <div class="flex items-center gap-3">
            <span class="w-8 h-8 rounded-xl bg-alt-background border border-secondary-border/70 text-secondary-text dark:text-zinc-300 flex items-center justify-center text-sm shadow-xs">
              <i class="fad fa-database text-xs"></i>
            </span>
            <div class="flex flex-col leading-tight">
              <span class="text-base font-bold text-heading tabular-nums">{{ uniqueDomains().length }}</span>
              <span class="text-[11px] text-secondary-text font-medium">Databases</span>
            </div>
          </div>
          <div class="w-px h-6 bg-primary-border"></div>
          <div class="flex items-center gap-3">
            <span class="w-8 h-8 rounded-xl bg-alt-background border border-secondary-border/70 text-secondary-text dark:text-zinc-300 flex items-center justify-center text-sm shadow-xs">
              <i class="fas fa-table text-xs"></i>
            </span>
            <div class="flex flex-col leading-tight">
              <span class="text-base font-bold text-heading tabular-nums">{{ models().length }}</span>
              <span class="text-[11px] text-secondary-text font-medium">Tables</span>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- FREE TIER SCHEMA GOVERNANCE BANNER -->
    @if (isFreeTier()) {
      <div class="flex items-center gap-3 px-4 py-3 rounded-xl bg-info-background/20 border border-info-border text-info-text text-xs shadow-xs">
        <i class="fas fa-shield-alt text-info text-sm shrink-0"></i>
        <div class="flex-1 leading-relaxed">
          <strong>Managed Multi-Tenant Schema:</strong> Tables in Free Tier workspaces are automatically managed by applications installed from the <strong>App Store</strong>. Custom creation, direct modification, or deletion of models and database schemas requires upgrading to a Paid workspace.
        </div>
      </div>
    }

    <!-- SEARCH & CONTROLS TOOLBAR -->
    <div class="flex flex-col @md:flex-row gap-3 justify-between items-stretch @md:items-center bg-component-inner/90 backdrop-blur border border-primary-border rounded-2xl p-3 shadow-sm">
      <div class="flex-1 max-w-xl">
        <nz-input-group [nzPrefix]="searchIcon" class="w-full !rounded-xl overflow-hidden border-secondary-border focus-within:ring-2 focus-within:ring-primary/20">
          <input
            type="text"
            nz-input
            [placeholder]="config.texts.searchPlaceholder"
            [ngModel]="searchQuery()"
            (ngModelChange)="searchQuery.set($event)"
            class="bg-input-background text-primary-text border-none focus:ring-0 text-sm py-2"
          />
        </nz-input-group>
        <ng-template #searchIcon>
          <i class="fas fa-search text-secondary-text text-sm ml-1 mr-2"></i>
        </ng-template>
      </div>

      <div class="flex items-center gap-2 shrink-0">
        <button
          (click)="toggleAllGroups()"
          class="text-xs font-semibold px-3.5 py-2 rounded-xl bg-alt-background border border-secondary-border text-secondary-text hover:text-primary hover:border-primary transition-all flex items-center gap-2 shadow-sm whitespace-nowrap cursor-pointer"
        >
          <i
            class="fas"
            [ngClass]="areAllGroupsExpanded() ? 'fa-compress-alt' : 'fa-expand-alt'"
          ></i>
          <span>{{ areAllGroupsExpanded() ? config.texts.collapseAll : config.texts.expandAll }}</span>
        </button>
      </div>
    </div>

    <!-- DATABASE / DOMAIN FILTER CHIPS -->
    <div class="flex flex-wrap items-center gap-2 shrink-0">
      <button
        type="button"
        (click)="selectDomain(null)"
        class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all border cursor-pointer"
        [ngClass]="selectedDomain() === null
          ? 'bg-active-pill text-primary-text border-secondary-border shadow-sm'
          : 'bg-component-inner border-primary-border text-secondary-text hover:text-primary-text hover:border-secondary-border'"
      >
        <i
          class="fad fa-layer-group text-[11px]"
          [ngClass]="selectedDomain() === null ? 'text-primary-text' : 'text-secondary-text'"
        ></i>
        <span>{{ config.texts.allDomains }}</span>
        <span
          class="text-[10px] font-bold px-1.5 py-0.2 rounded-full"
          [ngClass]="selectedDomain() === null ? 'bg-component-inner text-primary-text border border-secondary-border/40 shadow-xs' : 'bg-alt-background text-secondary-text'"
        >{{ models().length }}</span>
      </button>

      @for (domain of uniqueDomains(); track domain.key) {
        <button
          type="button"
          (click)="selectDomain(domain.key)"
          class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all border cursor-pointer"
          [ngClass]="selectedDomain() === domain.key
            ? 'bg-active-pill text-primary-text border-secondary-border shadow-sm'
            : 'bg-component-inner border-primary-border text-secondary-text hover:text-primary-text hover:border-secondary-border'"
        >
          <i
            class="fad fa-database text-[11px]"
            [ngClass]="selectedDomain() === domain.key ? 'text-primary-text' : 'text-secondary-text'"
          ></i>
          <span>{{ domain.name }}</span>
          <span
            class="text-[10px] font-mono px-1.5 py-0.2 rounded-md font-bold"
            [ngClass]="selectedDomain() === domain.key ? 'bg-component-inner text-primary-text border border-secondary-border/40 shadow-xs' : 'bg-alt-background text-secondary-text'"
          >{{ domainCounts()[domain.key] || 0 }}</span>
        </button>
      }
    </div>

    <!-- BODY CONTENT -->
    @if (isLoading()) {
      <div class="flex flex-col items-center justify-center p-20 w-full flex-1 gap-4">
        <i class="fas fa-circle-notch fa-spin text-4xl text-primary"></i>
        <p class="text-sm text-secondary-text font-medium">Loading databases &amp; schemas...</p>
      </div>
    } @else if (filteredGroupedModels().length === 0) {
      <div class="text-center p-14 bg-component-inner border border-primary-border rounded-2xl shadow-sm w-full max-w-xl mx-auto mt-6">
        <div class="w-16 h-16 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400 mx-auto flex items-center justify-center text-3xl mb-4">
          <i class="fad fa-database"></i>
        </div>
        <h3 class="text-lg font-bold text-heading">No Tables Found</h3>
        <p class="text-secondary-text text-sm mt-1 max-w-sm mx-auto">
          {{ config.texts.emptyState }}
        </p>
      </div>
    } @else {
      <!-- DATABASES ACCORDION CONTAINER -->
      <div class="flex flex-col gap-6 max-w-full w-full mx-auto mt-1 mb-12">
        @for (group of filteredGroupedModels(); track group.domainKey) {
          <section
            class="bg-component-inner border border-primary-border rounded-2xl shadow-sm overflow-hidden transition-all duration-300 hover:border-primary/40 flex flex-col"
          >
            <!-- DATABASE HEADER CARD -->
            <div
              class="p-4 @md:p-5 flex items-center justify-between gap-4 cursor-pointer select-none bg-component-inner hover:bg-hover-default/60 transition-colors"
              (click)="toggleGroup(group.domainKey)"
            >
              <div class="flex items-center gap-3.5 min-w-0">
                <!-- Database Tile Icon -->
                <div
                  class="w-11 h-11 rounded-xl bg-alt-background border border-secondary-border/70 text-secondary-text dark:text-zinc-300 flex items-center justify-center shrink-0 shadow-xs"
                >
                  <i class="fad fa-database text-lg"></i>
                </div>

                <!-- Database Title & Schema Badges -->
                <div class="flex flex-col min-w-0">
                  <div class="flex items-center gap-2.5 flex-wrap">
                    <h2 class="text-lg @md:text-xl font-bold text-heading hover:text-primary transition-colors truncate m-0">
                      {{ group.domainName }}
                    </h2>
                    <span
                      class="inline-flex items-center gap-1 font-mono text-[11px] px-2 py-0.5 rounded-md border border-secondary-border bg-alt-bold-background text-secondary-text shrink-0"
                      title="Database Schema Key"
                    >
                      <i class="fas fa-key text-[9px] opacity-60"></i>{{ group.domainKey }}
                    </span>
                  </div>

                  <div class="flex items-center gap-3 mt-1 text-xs text-secondary-text">
                    <span class="inline-flex items-center gap-1.5 font-medium text-primary-text">
                      <i class="fas fa-table text-secondary-text text-xs"></i>
                      <strong>{{ group.models.length }}</strong>
                      {{ group.models.length === 1 ? 'Table' : 'Tables' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Header Right Controls -->
              <div class="flex items-center gap-1.5 shrink-0" (click)="$event.stopPropagation()">
                <!-- Database Actions 3-Dots Menu (Hidden for Free Tier) -->
                @if (!isFreeTier()) {
                  <button
                    nz-button
                    nzType="text"
                    nz-dropdown
                    [nzDropdownMenu]="domainActionsMenu"
                    nzTrigger="click"
                    nzPlacement="bottomRight"
                    title="Database Actions"
                    class="w-8 h-8 !p-0 rounded-lg border border-secondary-border/60 bg-alt-background/60 flex items-center justify-center text-secondary-text hover:text-primary hover:bg-hover-default transition-colors cursor-pointer"
                  >
                    <i class="fas fa-ellipsis-v text-xs"></i>
                  </button>

                  <nz-dropdown-menu #domainActionsMenu="nzDropdownMenu">
                    <ul nz-menu class="!rounded-xl shadow-lg border border-primary-border min-w-44 py-1">
                      <li
                        nz-menu-item
                        nzDanger
                        (click)="openDeleteGroupModal(group, $event)"
                        class="flex items-center gap-2 text-xs font-semibold py-2 px-3 hover:bg-danger/10 text-danger"
                      >
                        <i class="fas fa-trash-alt text-xs"></i>
                        <span>Delete Database</span>
                      </li>
                    </ul>
                  </nz-dropdown-menu>
                }

                <!-- Collapse / Expand Chevron -->
                <div
                  (click)="toggleGroup(group.domainKey)"
                  class="w-8 h-8 rounded-lg border border-secondary-border/60 bg-alt-background/60 flex items-center justify-center text-secondary-text hover:text-primary transition-transform duration-200 cursor-pointer"
                  [ngClass]="expandedGroups()[group.domainKey] !== false ? 'rotate-180' : ''"
                >
                  <i class="fas fa-chevron-down text-xs"></i>
                </div>
              </div>
            </div>

            <!-- TABLES CONTAINER (WHEN DATABASE IS EXPANDED) -->
            @if (expandedGroups()[group.domainKey] !== false) {
              <div class="p-4 @md:p-6 pt-2 border-t border-primary-border/60 bg-body-background/40">
                @if (group.models.length === 0) {
                  <div class="text-center py-8 text-secondary-text text-sm">
                    No tables defined in this database yet.
                  </div>
                } @else {
                  <div class="grid grid-cols-1 @md:grid-cols-2 @xl:grid-cols-2 @4xl:grid-cols-3 gap-4">
                    @for (model of group.models; track model.id) {
                      <div
                        role="button"
                        tabindex="0"
                        (click)="goToDetails(model.id)"
                        (keydown.enter)="goToDetails(model.id)"
                        class="w-full text-left bg-component-inner border border-primary-border rounded-xl p-4 shadow-sm flex flex-col justify-between transition-all duration-300 hover:border-primary hover:shadow-default hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-focus-ring group cursor-pointer"
                      >
                        <!-- Table Card Top -->
                        <div class="flex items-start gap-3.5 w-full">
                          <!-- Table Icon -->
                          <div
                            class="w-10 h-10 rounded-xl bg-alt-background border border-secondary-border/70 text-secondary-text dark:text-zinc-300 flex items-center justify-center shrink-0 shadow-xs group-hover:scale-105 group-hover:border-secondary-border group-hover:text-primary-text transition-all"
                          >
                            <i class="fas fa-table text-sm"></i>
                          </div>

                          <div class="flex flex-col flex-1 min-w-0">
                            <div class="flex items-center justify-between gap-2">
                              <h3 class="text-base font-bold text-heading group-hover:text-primary transition-colors truncate m-0">
                                {{ model.title }}
                              </h3>
                            </div>

                            <!-- Physical Table Name Badge -->
                            <div class="flex items-center gap-1.5 mt-0.5">
                              <span class="text-[11px] font-mono text-secondary-text bg-alt-bold-background px-2 py-0.5 rounded border border-secondary-border/60 truncate">
                                tbl: {{ model.dataTableName || model.name }}
                              </span>
                            </div>

                            <!-- Table Description -->
                            <p class="text-xs text-secondary-text line-clamp-2 mt-2 mb-0 leading-relaxed">
                              {{ model.description || config.texts.noDescription }}
                            </p>
                          </div>
                        </div>

                        <!-- Table Card Footer Bar -->
                        <div class="flex items-center justify-between pt-3 mt-3 border-t border-primary-border/60 text-xs">
                          <span class="inline-flex items-center gap-1 text-[11px] font-semibold text-secondary-text">
                            <i class="fas fa-table text-secondary-text text-[10px]"></i> Relational Table
                          </span>

                          <span class="inline-flex items-center gap-1 text-primary text-xs font-bold opacity-80 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
                            <span>Open Table</span>
                            <i class="fas fa-arrow-right text-[10px]"></i>
                          </span>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </section>
        }
      </div>
    }

    <nz-modal
      [(nzVisible)]="isAiContextModalVisible"
      [nzTitle]="config.texts.aiContextModalTitle"
      [nzWidth]="800"
      (nzOnCancel)="closeAiContextModal()"
      [nzFooter]="aiContextModalFooter"
    >
      <ng-container *nzModalContent>
        @if (isAiContextLoading()) {
          <div
            class="flex flex-col items-center justify-center p-12 h-64 gap-4"
          >
            <i class="fas fa-circle-notch fa-spin text-4xl text-primary"></i>
            <span
              class="text-secondary-text font-medium"
              i18n="@@database.aiContextLoading"
              >{{ config.texts.aiContextLoading }}</span
            >
          </div>
        } @else {
          <div
            class="w-full max-h-[60vh] overflow-y-auto custom-scrollbar bg-alt-bold-background border border-secondary-border rounded-lg p-4 shadow-inner"
          >
            <pre
              class="whitespace-pre-wrap font-mono text-sm text-primary-text m-0"
              >{{ aiContextContent() }}</pre
            >
          </div>
        }
      </ng-container>
      <ng-template #aiContextModalFooter>
        <div class="flex justify-end gap-3 mt-2">
          <button
            nz-button
            nzType="default"
            (click)="closeAiContextModal()"
            class="border-secondary-border text-secondary-text hover:text-primary transition-colors font-semibold"
          >
            {{ config.texts.aiContextModalClose }}
          </button>
          <button
            nz-button
            nzType="primary"
            (click)="copyAiContextToClipboard()"
            [disabled]="isAiContextLoading()"
            class="bg-primary text-primary-button-text hover:bg-hover-primary border-none shadow-sm flex items-center gap-2 font-bold transition-all"
          >
            <i
              class="fas"
              [ngClass]="isCopied() ? 'fa-check text-success' : 'fa-copy'"
            ></i>
            <span>{{
              isCopied()
                ? config.texts.aiContextModalCopied
                : config.texts.aiContextModalCopy
            }}</span>
          </button>
        </div>
      </ng-template>
    </nz-modal>

    <!-- Cascade Deletion Approval Modal -->
    <nz-modal
      [(nzVisible)]="isDeleteModalVisible"
      [nzTitle]="deleteModalTitle"
      [nzWidth]="620"
      (nzOnCancel)="closeDeleteModal()"
      [nzFooter]="deleteModalFooter"
    >
      <ng-template #deleteModalTitle>
        <div class="flex items-center gap-2 text-danger">
          <i class="fas fa-exclamation-triangle text-xl"></i>
          <span class="text-base font-bold text-heading">
            @if (deleteTargetType() === 'group') {
              Cascade Delete Group: {{ groupToDelete()?.domainName }}
            } @else {
              Cascade Delete Table: {{ modelToDelete()?.title || modelToDelete()?.name }}
            }
          </span>
        </div>
      </ng-template>

      <ng-container *nzModalContent>
        <div class="space-y-4 py-1">
          <!-- Critical Warning Banner -->
          <div class="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-900 flex gap-3 shadow-sm">
            <i class="fas fa-triangle-exclamation text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
            <div class="space-y-2.5">
              <p class="font-bold text-red-950 text-sm">
                Permanent and Cascading Deletion
              </p>
              <p class="text-xs text-red-900 leading-relaxed">
                Deleting this @if (deleteTargetType() === 'group') { group } @else { table } will permanently delete the following components from your workspace:
              </p>
              <ul class="list-disc list-inside text-xs text-red-800 space-y-1 pl-1">
                <li><strong>Data Model & Database Table:</strong> Schema definition, storage collections, and all rows/records</li>
                <li><strong>Endpoints & API Definitions:</strong> All auto-generated CRUD routes, custom endpoints, and OpenAPI data schemas</li>
                <li><strong>Relations & Code Deployments:</strong> Inter-model relations, UI step function bindings, and serverless deployments</li>
              </ul>
              <div class="pt-2 border-t border-red-200/80">
                <p class="text-xs font-bold text-red-950 flex items-center gap-1.5">
                  <i class="fas fa-ban text-red-600"></i> Impact on Applications:
                </p>
                <p class="text-xs text-red-800 mt-0.5 leading-relaxed">
                  If there are applications using this database, <strong>they will become unusable</strong> until you recreate or redeploy these databases.
                </p>
              </div>
            </div>
          </div>

          <!-- Target Breakdown -->
          <div class="bg-alt-bold-background border border-secondary-border rounded-xl p-3.5 space-y-2">
            @if (deleteTargetType() === 'group') {
              <div class="flex items-center justify-between text-xs">
                <span class="text-secondary-text font-medium">Domain Group:</span>
                <span class="font-bold text-heading">{{ groupToDelete()?.domainName }} (<code class="font-mono text-primary font-bold">{{ groupToDelete()?.domainKey }}</code>)</span>
              </div>
              <div class="text-xs">
                <span class="text-secondary-text font-medium block mb-1.5">Affected Tables ({{ groupToDelete()?.models?.length || 0 }}):</span>
                <div class="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto custom-scrollbar">
                  @for (m of groupToDelete()?.models; track m.id) {
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-component-inner border border-primary-border text-primary-text">
                      <i class="fas fa-table mr-1 text-secondary-text text-[10px]"></i>
                      {{ m.title || m.name }}
                    </span>
                  }
                </div>
              </div>
            } @else {
              <div class="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span class="text-secondary-text font-medium block">Table Name:</span>
                  <span class="font-bold text-heading text-sm">{{ modelToDelete()?.title || modelToDelete()?.name }}</span>
                </div>
                <div>
                  <span class="text-secondary-text font-medium block">System Identifier:</span>
                  <code class="font-mono text-primary font-medium text-xs">{{ modelToDelete()?.name }}</code>
                </div>
              </div>
            }
          </div>

          <!-- Typing Approval Input -->
          <div class="space-y-1.5 pt-1">
            <label class="block text-xs font-semibold text-heading">
              To approve this cascade deletion, type
              <span class="font-mono font-bold text-danger bg-red-50 border border-red-200 px-1.5 py-0.5 rounded select-all">
                "{{ expectedConfirmationText() }}"
              </span>
              to confirm:
            </label>
            <input
              nz-input
              type="text"
              class="w-full !rounded-lg text-sm !py-2 border-secondary-border focus:!border-danger focus:!ring-danger/20"
              [placeholder]="'Type \'' + expectedConfirmationText() + '\' to confirm'"
              [ngModel]="deleteConfirmationInput()"
              (ngModelChange)="deleteConfirmationInput.set($event)"
              (keydown.enter)="isDeleteConfirmed() && !isDeleting() && confirmCascadeDelete()"
            />
          </div>
        </div>
      </ng-container>

      <ng-template #deleteModalFooter>
        <div class="flex items-center justify-end gap-2.5 pt-2">
          <button
            nz-button
            nzType="default"
            (click)="closeDeleteModal()"
            [disabled]="isDeleting()"
            class="rounded-lg text-secondary-text font-medium border-secondary-border"
          >
            Cancel
          </button>
          <button
            nz-button
            nzDanger
            nzType="primary"
            (click)="confirmCascadeDelete()"
            [disabled]="!isDeleteConfirmed() || isDeleting()"
            [nzLoading]="isDeleting()"
            class="font-semibold rounded-lg shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-45 disabled:cursor-not-allowed"
            [class.!bg-danger]="isDeleteConfirmed() && !isDeleting()"
            [class.!text-white]="isDeleteConfirmed() && !isDeleting()"
            [class.!bg-gray-200]="!isDeleteConfirmed() && !isDeleting()"
            [class.!text-gray-400]="!isDeleteConfirmed() && !isDeleting()"
            [style.color]="(isDeleteConfirmed() && !isDeleting()) ? '#ffffff !important' : '#9ca3af !important'"
          >
            <i class="fas fa-trash-alt mr-1"></i>
            <span>
              @if (deleteTargetType() === 'group') {
                Delete Group & All Tables
              } @else {
                Delete Table & Endpoints
              }
            </span>
          </button>
        </div>
      </ng-template>
    </nz-modal>
  </div>
</div>
